import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Plus, Minus, Check, Package, ArrowLeft, ArrowUpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from '../components/sections/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Shop = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [packages, setPackages] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeL1, setActiveL1] = useState('');
  const [activeL2, setActiveL2] = useState('');
  const [cart, setCart] = useState([]);
  const t = (en, zh) => isZh ? (zh || en) : en;

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { toast.error(isZh ? '请登录系统' : 'Please login first'); navigate('/profile', { state: { returnTo: '/shop' } }); return; }
    const load = async () => {
      try {
        const [pkgR, prodR, catR] = await Promise.all([
          axios.get(`${API}/shop/packages`), axios.get(`${API}/shop/products`), axios.get(`${API}/shop/categories`)
        ]);
        setPackages(pkgR.data.packages || []);
        setProducts(prodR.data.products || []);
        setCategories(catR.data.categories || []);
        setCart(JSON.parse(localStorage.getItem('shop_cart') || '[]'));
      } catch {}
    };
    load();
  }, []);

  const l1List = [...new Set(categories.map(c => c.level1_en).filter(Boolean))];
  const l2List = activeL1 ? [...new Set(categories.filter(c => c.level1_en === activeL1).map(c => c.level2_en).filter(Boolean))] : [];

  const filteredProducts = products.filter(p => {
    if (activeL1 && p.category_level1 !== activeL1) return false;
    if (activeL2 && p.category_level2 !== activeL2) return false;
    return true;
  });

  const showPrice = (p) => {
    if (p.discount_price > 0) return <><span className="text-red-600 font-bold text-lg">${p.discount_price}</span>{p.original_price > 0 && <span className="text-gray-400 line-through text-sm ml-2">${p.original_price}</span>}</>;
    if (p.original_price > 0) return <span className="text-gray-900 font-bold text-lg">${p.original_price}</span>;
    if (p.package_price > 0) return <span className="text-red-600 font-bold text-lg">${p.package_price}</span>;
    return null;
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <section className="bg-red-600 text-white py-12 pt-28">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <div><h1 className="text-4xl font-bold mb-2">Order Material Selection</h1><p className="text-gray-400">{isZh ? '选择您的建材和套餐' : 'Select your building materials and packages'}</p></div>
          <Button onClick={() => navigate('/shop/cart')} variant="outline" className="border-white text-white hover:bg-white/10 relative">
            <ShoppingCart className="w-5 h-5 mr-2" />{isZh ? '购物车' : 'Cart'}
            {cart.length > 0 && <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-600 text-white text-xs rounded-full flex items-center justify-center">{cart.length}</span>}
          </Button>
        </div>
      </section>
      <main className="flex-grow">
        {/* Packages */}
        {packages.filter(p => p.is_featured !== false).length > 0 && (
          <section className="py-12 bg-gray-50">
            <div className="max-w-6xl mx-auto px-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{isZh ? '精选套餐' : 'Featured Packages'}</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 justify-items-center">
                {packages.filter(p => p.is_featured !== false).map(pkg => (
                  <Card key={pkg.id} className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-shadow cursor-pointer group w-full" onClick={() => navigate(`/shop/package/${pkg.id}`)}>
                    <div className="h-48 overflow-hidden">{pkg.image ? <img src={pkg.image} alt="" className="w-full h-full object-contain bg-gray-50 group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full bg-red-100 flex items-center justify-center"><Package className="w-16 h-16 text-red-300" /></div>}</div>
                    <CardContent className="p-4"><h3 className="font-bold text-gray-900">{t(pkg.name_en, pkg.name_zh)}</h3><div className="mt-2">{showPrice(pkg)}</div></CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        )}
        {/* Products with L1 + L2 tabs */}
        <section className="py-12">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex flex-wrap gap-3 mb-4">
              <button onClick={() => { setActiveL1(''); setActiveL2(''); }} className={`px-6 py-2.5 rounded-lg text-base font-bold ${!activeL1 ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600'}`}>{isZh ? '全部' : 'All'}</button>
              {l1List.map(l1 => <button key={l1} onClick={() => { setActiveL1(l1); setActiveL2(''); }} className={`px-6 py-2.5 rounded-lg text-base font-bold ${activeL1 === l1 ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600'}`}>{l1}</button>)}
            </div>
            {l2List.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                <button onClick={() => setActiveL2('')} className={`px-3 py-1 rounded text-xs font-medium ${!activeL2 ? 'bg-gray-800 text-white' : 'bg-gray-50 text-gray-500 border'}`}>{isZh ? '全部' : 'All'}</button>
                {l2List.map(l2 => <button key={l2} onClick={() => setActiveL2(l2)} className={`px-3 py-1 rounded text-xs font-medium ${activeL2 === l2 ? 'bg-gray-800 text-white' : 'bg-gray-50 text-gray-500 border'}`}>{l2}</button>)}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
              {filteredProducts.map(p => (
                <Card key={p.id} className="overflow-hidden border shadow-sm hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => navigate(`/shop/product/${p.id}`)}>
                  <div className="h-52 bg-gray-50 flex items-center justify-center overflow-hidden"><img src={p.main_image || ''} alt="" className="max-w-full max-h-full object-contain" /></div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-base text-gray-900 line-clamp-1">{t(p.name_en, p.name_zh)}</h3>
                    <div className="mt-2">{showPrice(p)}</div>
                    {p.supplier_en && <p className="text-xs text-gray-400 mt-1">{t(p.supplier_en, p.supplier_zh)}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

// ===== PRODUCT DETAIL =====
export const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [product, setProduct] = useState(null);
  const [selectedSpec, setSelectedSpec] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const t = (en, zh) => isZh ? (zh || en) : en;

  useEffect(() => {
    const load = async () => {
      try { const r = await axios.get(`${API}/shop/products`); setProduct((r.data.products || []).find(p => p.id === id)); } catch {}
    };
    load();
  }, [id]);

  if (!product) return <div className="min-h-screen flex items-center justify-center pt-20"><div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin" /></div>;

  const displayImage = selectedSpec?.image || product.main_image;
  const price = product.discount_price || product.original_price || product.package_price || 0;

  const addToCart = () => {
    const item = { product_id: product.id, name_en: product.name_en, name_zh: product.name_zh, spec_name: selectedSpec ? t(selectedSpec.name_en, selectedSpec.name_zh) : '', spec_image: displayImage, price, quantity };
    const cart = JSON.parse(localStorage.getItem('shop_cart') || '[]');
    localStorage.setItem('shop_cart', JSON.stringify([...cart, item]));
    toast.success(isZh ? '已加入购物车' : 'Added to cart');
    navigate('/shop');
  };

  return (
    <div className="min-h-screen bg-white flex flex-col pt-20">
      <main className="flex-grow py-10">
        <div className="max-w-5xl mx-auto px-6">
          <button onClick={() => navigate('/shop')} className="text-gray-500 hover:text-red-600 text-sm mb-6 flex items-center gap-1"><ArrowLeft className="w-4 h-4" />{isZh ? '返回' : 'Back'}</button>
          <div className="grid lg:grid-cols-2 gap-10">
            {/* Image — sticky */}
            <div className="lg:sticky lg:top-24 lg:self-start rounded-xl overflow-hidden bg-gray-50">
              <img src={displayImage} alt="" className="w-full object-contain" />
            </div>
            {/* Info — scrollable */}
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">{t(product.name_en, product.name_zh)}</h1>
              <div className="mb-4">
                {product.discount_price > 0 && <span className="text-3xl font-black text-red-600">${product.discount_price}</span>}
                {product.original_price > 0 && <span className={`text-xl ${product.discount_price > 0 ? 'text-gray-400 line-through ml-3' : 'text-gray-900 font-bold'}`}>${product.original_price}</span>}
                {product.package_price > 0 && <span className="text-sm text-gray-500 ml-3">{isZh ? '套餐价' : 'Package'}: ${product.package_price}</span>}
              </div>
              {product.supplier_en && <p className="text-gray-500 text-sm mb-4">{t(product.supplier_en, product.supplier_zh)}</p>}
              {product.description_en && <p className="text-gray-700 mb-6">{t(product.description_en, product.description_zh)}</p>}
              {/* Specs */}
              {product.specs?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-gray-600 mb-2">{isZh ? '选择规格' : 'Select Specification'}</h3>
                  <div className="flex flex-wrap gap-3">
                    {product.specs.map((spec, i) => (
                      <button key={i} onClick={() => setSelectedSpec(spec)}
                        className={`border rounded-xl p-3 text-left transition-all ${selectedSpec?.name_en === spec.name_en ? 'border-red-600 bg-red-50 ring-2 ring-red-200' : 'border-gray-200 hover:border-gray-400'}`}>
                        {spec.image && <img src={spec.image} alt="" className="w-24 h-auto object-contain rounded mb-1" />}
                        <p className="text-sm font-medium">{t(spec.name_en, spec.name_zh)}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* Quantity */}
              <div className="flex items-center gap-3 mb-6">
                <span className="text-sm font-bold text-gray-600">{isZh ? '数量' : 'Quantity'}:</span>
                <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="w-8 h-8 border rounded flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                <span className="text-lg font-bold w-10 text-center">{quantity}</span>
                <button onClick={() => setQuantity(q => q + 1)} className="w-8 h-8 border rounded flex items-center justify-center"><Plus className="w-4 h-4" /></button>
              </div>
              <Button onClick={addToCart} className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-3 text-lg w-full">
                <ShoppingCart className="w-5 h-5 mr-2" />{isZh ? '加入购物车' : 'Add to Cart'} · ${(price * quantity).toFixed(2)}
              </Button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

// ===== CART PAGE =====
export const ShopCart = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [cart, setCart] = useState(JSON.parse(localStorage.getItem('shop_cart') || '[]'));
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [specPicker, setSpecPicker] = useState(null); // index in cart
  const t = (en, zh) => isZh ? (zh || en) : en;
  const email = localStorage.getItem('admin_email') || '';

  useEffect(() => {
    const load = async () => { try { const r = await axios.get(`${API}/shop/products`); setProducts(r.data.products || []); } catch {} };
    load();
  }, []);

  const pkgItems = cart.filter(i => i.fromPackage);
  const extraItems = cart.filter(i => !i.fromPackage);
  const [selected, setSelected] = useState(new Set()); // cart indexes
  const saveCart = (c, keepSel) => { setCart(c); localStorage.setItem('shop_cart', JSON.stringify(c)); if (!keepSel) setSelected(new Set(c.map((_, i) => i))); };

  // Initialize: select all by default
  useEffect(() => { setSelected(new Set(cart.map((_, i) => i))); }, [cart.length]);

  const removePackage = () => { if (window.confirm(isZh ? '删除整个套餐？' : 'Remove entire package?')) saveCart(cart.filter(i => !i.fromPackage)); };
  const removeExtra = (item) => { saveCart(cart.filter(i => i !== item)); };
  const removeSelected = () => {
    if (selected.size === 0) return;
    const extra = [...selected].filter(i => !cart[i]?.fromPackage);
    if (extra.length === 0) return;
    saveCart(cart.filter((_, i) => !extra.includes(i)));
  };
  const toggleSelect = (idx) => {
    setSelected(prev => { const s = new Set(prev); if (s.has(idx)) s.delete(idx); else s.add(idx); return s; });
  };
  const selectAllPkg = (check) => {
    setSelected(prev => {
      const s = new Set(prev);
      cart.forEach((item, i) => { if (item.fromPackage) { if (check) s.add(i); else s.delete(i); } });
      return s;
    });
  };
  const selectAllExtra = (check) => {
    setSelected(prev => {
      const s = new Set(prev);
      cart.forEach((item, i) => { if (!item.fromPackage) { if (check) s.add(i); else s.delete(i); } });
      return s;
    });
  };

  const selectSpec = (cartIdx, spec) => {
    const c = [...cart];
    c[cartIdx] = { ...c[cartIdx], spec_name: t(spec.name_en, spec.name_zh), spec_image: spec.image || c[cartIdx].spec_image };
    saveCart(c, true);
    setSpecPicker(null);
  };

  const [upgradeModal, setUpgradeModal] = useState(null); // {cartIdx, product}
  const [upgradeSpec, setUpgradeSpec] = useState(null);

  const getUpgradeProducts = (prodId) => {
    const prod = products.find(p => p.id === prodId);
    if (!prod?.is_upgradable || !prod.upgrade_product_ids?.length) return [];
    return prod.upgrade_product_ids.map(uid => products.find(p => p.id === uid)).filter(Boolean);
  };

  const doUpgrade = (cartIdx, upgradeProd, spec) => {
    const c = [...cart];
    const original = c[cartIdx];
    c[cartIdx] = {
      ...original,
      product_id: upgradeProd.id,
      name_en: upgradeProd.name_en, name_zh: upgradeProd.name_zh,
      spec_name: spec ? t(spec.name_en, spec.name_zh) : '',
      spec_image: spec?.image || upgradeProd.main_image,
      price: upgradeProd.original_price || upgradeProd.discount_price || 0,
      upgraded: true,
      original_item: { product_id: original.product_id, name_en: original.name_en, name_zh: original.name_zh, spec_name: original.spec_name, spec_image: original.spec_image, price: original.price }
    };
    saveCart(c, true);
    setUpgradeModal(null);
    setUpgradeSpec(null);
    toast.success(isZh ? '已升级' : 'Upgraded');
  };

  const doRestore = (cartIdx) => {
    const c = [...cart];
    const item = c[cartIdx];
    if (!item.original_item) return;
    c[cartIdx] = { ...item, ...item.original_item, upgraded: false, original_item: undefined };
    saveCart(c, true);
    toast.success(isZh ? '已恢复' : 'Restored');
  };

  const selectedTotal = cart.filter((_, i) => selected.has(i)).reduce((s, i) => s + (i.price || 0) * (i.quantity || 1), 0);

  const placeOrder = async () => {
    const orderItems = cart.filter((_, i) => selected.has(i));
    if (orderItems.length === 0) { toast.error(isZh ? '请选择商品' : 'Please select items'); return; }
    setLoading(true);
    try {
      const { getUserProfile } = await import('../utils/userProfile');
      const prof = await getUserProfile();
      const r = await axios.post(`${API}/shop/orders`, { items: orderItems, phone: prof?.phone || '', email, address: prof?.address || '', notes: '', account: email });
      toast.success(`${isZh ? '订单已生成' : 'Order placed'}: ${r.data.order_no}`);
      // Remove ordered items from cart, keep unselected
      const remaining = cart.filter((_, i) => !selected.has(i));
      saveCart(remaining);
      navigate('/user-profile');
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const getSpecs = (prodId) => products.find(p => p.id === prodId)?.specs || [];

  const renderItem = (item, cartIdx, type) => {
    const specs = getSpecs(item.product_id);
    const upgrades = type === 'pkg' && !item.upgraded ? getUpgradeProducts(item.product_id) : [];
    return (
      <Card key={cartIdx} className="shadow-sm"><CardContent className="p-4 flex items-center gap-4">
        <input type="checkbox" checked={selected.has(cartIdx)} onChange={() => toggleSelect(cartIdx)} className="accent-red-600 w-4 h-4" />
        {item.spec_image && <img src={item.spec_image} alt="" className="w-32 h-32 rounded-lg object-contain bg-gray-50 hover-zoom-img" />}
        <div className="flex-1">
          <h3 className="font-bold text-sm">{t(item.name_en, item.name_zh)}{item.upgraded && <span className="text-xs text-amber-600 ml-1">↑{isZh ? '已升级' : 'Upgraded'}</span>}</h3>
          {item.spec_name && <p className="text-xs text-gray-500">{item.spec_name}</p>}
          <p className="text-xs text-gray-400">x{item.quantity || 1}</p>
        </div>
        {specs.length > 0 && <button onClick={() => setSpecPicker(specPicker === cartIdx ? null : cartIdx)} className="text-xs px-2 py-1 border rounded hover:border-red-600 hover:text-red-600">{isZh ? '规格' : 'Spec'}</button>}
        {upgrades.length > 0 && <button onClick={() => setUpgradeModal({ cartIdx, upgrades })} className="text-xs px-2 py-1 border border-amber-400 text-amber-600 rounded hover:bg-amber-50">↑ {isZh ? '升级' : 'Upgrade'}</button>}
        {item.upgraded && item.original_item && <button onClick={() => doRestore(cartIdx)} className="text-xs px-2 py-1 border border-blue-400 text-blue-600 rounded hover:bg-blue-50">↩ {isZh ? '恢复' : 'Restore'}</button>}
        <p className="font-bold text-red-600">${((item.price || 0) * (item.quantity || 1)).toFixed(2)}</p>
        {type === 'extra' && <button onClick={() => removeExtra(item)} className="text-gray-400 hover:text-red-600 text-xl">&times;</button>}
      </CardContent>
      {specPicker === cartIdx && (
        <div className="px-4 pb-4 flex flex-wrap gap-2">
          {specs.map((spec, si) => (
            <button key={si} onClick={() => selectSpec(cartIdx, spec)} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-xs ${item.spec_name === t(spec.name_en, spec.name_zh) ? 'border-red-600 bg-red-50' : 'border-gray-200'}`}>
              {spec.image && <img src={spec.image} alt="" className="w-8 h-auto object-contain rounded" />}
              <span>{t(spec.name_en, spec.name_zh)}</span>
            </button>
          ))}
        </div>
      )}
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pt-20">
      <main className="flex-grow py-10">
        <div className="max-w-3xl mx-auto px-6">
          <h1 className="text-3xl font-bold mb-6">{isZh ? '购物车' : 'Shopping Cart'}</h1>
          {cart.length === 0 ? (
            <Card><CardContent className="p-12 text-center text-gray-400"><ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>{isZh ? '购物车为空' : 'Cart is empty'}</p><Button onClick={() => navigate('/shop')} className="mt-4 bg-red-600 text-white">{isZh ? '去选购' : 'Start Shopping'}</Button></CardContent></Card>
          ) : (
            <>
              {pkgItems.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-bold text-red-600 flex items-center gap-2">
                      <input type="checkbox" checked={pkgItems.every((_, i) => selected.has(cart.indexOf(pkgItems[i])))} onChange={e => selectAllPkg(e.target.checked)} className="accent-red-600 w-4 h-4" />
                      📦 {pkgItems[0]?.packageName || (isZh ? '套餐商品' : 'Package Items')}
                    </label>
                    <button onClick={removePackage} className="text-xs text-red-500 hover:text-red-700">{isZh ? '删除套餐' : 'Remove Package'}</button>
                  </div>
                  <div className="space-y-2">{pkgItems.map((item) => renderItem(item, cart.indexOf(item), 'pkg'))}</div>
                </div>
              )}
              {extraItems.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                      <input type="checkbox" checked={extraItems.every((_, i) => selected.has(cart.indexOf(extraItems[i])))} onChange={e => selectAllExtra(e.target.checked)} className="accent-red-600 w-4 h-4" />
                      {isZh ? '额外商品' : 'Additional Items'}
                    </label>
                    {extraItems.some((_, i) => selected.has(cart.indexOf(extraItems[i]))) && <button onClick={removeSelected} className="text-xs text-red-500 hover:text-red-700">{isZh ? '删除选中' : 'Delete Selected'}</button>}
                  </div>
                  <div className="space-y-2">{extraItems.map((item) => renderItem(item, cart.indexOf(item), 'extra'))}</div>
                </div>
              )}
              <Card className="shadow-lg"><CardContent className="p-6 flex items-center justify-between">
                <div><p className="text-gray-500">{isZh ? '已选合计' : 'Selected Total'} ({selected.size} {isZh ? '件' : 'items'})</p><p className="text-3xl font-black text-red-600">${selectedTotal.toFixed(2)}</p></div>
                <Button onClick={placeOrder} disabled={loading || selected.size === 0} className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-3 text-lg">{loading ? '...' : (isZh ? '提交订单' : 'Place Order')}</Button>
              </CardContent></Card>
            </>
          )}
        </div>
      </main>

      {/* Upgrade Modal */}
      {upgradeModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => { setUpgradeModal(null); setUpgradeSpec(null); }}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-4">{isZh ? '选择升级商品' : 'Select Upgrade Product'}</h3>
            <div className="space-y-3">
              {upgradeModal.upgrades.map(up => (
                <div key={up.id} className="border rounded-xl p-3">
                  <div className="flex items-center gap-3 mb-2">
                    <img src={upgradeSpec?.pid === up.id && upgradeSpec?.spec?.image ? upgradeSpec.spec.image : up.main_image} alt="" className="w-16 h-16 rounded object-contain bg-gray-50" />
                    <div className="flex-1"><h4 className="font-bold text-sm">{t(up.name_en, up.name_zh)}</h4><p className="text-red-600 font-bold">${up.original_price || up.discount_price || 0}</p></div>
                    <Button onClick={() => doUpgrade(upgradeModal.cartIdx, up, upgradeSpec?.pid === up.id ? upgradeSpec.spec : null)} className="bg-amber-500 hover:bg-amber-600 text-white text-xs px-3">↑ {isZh ? '升级' : 'Upgrade'}</Button>
                  </div>
                  {up.specs?.length > 0 && (
                    <div className="flex flex-wrap gap-2">{up.specs.map((spec, si) => (
                      <button key={si} onClick={() => setUpgradeSpec({ pid: up.id, spec })} className={`flex items-center gap-1 px-2 py-1 rounded border text-xs ${upgradeSpec?.pid === up.id && upgradeSpec?.spec?.name_en === spec.name_en ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}>
                        {spec.image && <img src={spec.image} alt="" className="w-6 h-auto object-contain rounded" />}<span>{t(spec.name_en, spec.name_zh)}</span>
                      </button>
                    ))}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

// ===== PACKAGE DETAIL with spec selection + upgrade =====
export const PackageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [pkg, setPkg] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedSpecs, setSelectedSpecs] = useState({});
  const t = (en, zh) => isZh ? (zh || en) : en;

  useEffect(() => {
    const load = async () => {
      try {
        const [pkgR, prodR] = await Promise.all([axios.get(`${API}/shop/packages`), axios.get(`${API}/shop/products`)]);
        setPkg((pkgR.data.packages || []).find(p => p.id === id));
        setProducts(prodR.data.products || []);
      } catch {}
    };
    load();
  }, [id]);

  if (!pkg) return <div className="min-h-screen flex items-center justify-center pt-20"><div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin" /></div>;

  const pkgProducts = (pkg.product_ids || []).map(pid => products.find(p => p.id === pid)).filter(Boolean);

  const addAllToCart = () => {
    const items = pkgProducts.map(p => ({
      product_id: p.id, name_en: p.name_en, name_zh: p.name_zh,
      spec_name: selectedSpecs[p.id] ? t(selectedSpecs[p.id].name_en, selectedSpecs[p.id].name_zh) : '',
      spec_image: selectedSpecs[p.id]?.image || p.main_image,
      price: p.package_price ?? 0, quantity: 1,
      fromPackage: true, packageName: t(pkg.name_en, pkg.name_zh)
    }));
    const cart = JSON.parse(localStorage.getItem('shop_cart') || '[]');
    localStorage.setItem('shop_cart', JSON.stringify([...cart, ...items]));
    toast.success(isZh ? '套餐已加入购物车' : 'Package added to cart');
    navigate('/shop/cart');
  };

  return (
    <div className="min-h-screen bg-white flex flex-col pt-20">
      <main className="flex-grow py-10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex justify-between items-start mb-8">
            <div>
              <button onClick={() => navigate('/shop')} className="text-gray-500 hover:text-red-600 text-sm mb-2 flex items-center gap-1"><ArrowLeft className="w-4 h-4" />{isZh ? '返回' : 'Back'}</button>
              <h1 className="text-3xl font-bold">{t(pkg.name_en, pkg.name_zh)}</h1>
              {pkg.description_en && <p className="text-gray-500 mt-2">{t(pkg.description_en, pkg.description_zh)}</p>}
            </div>
            <Button onClick={addAllToCart} className="bg-red-600 hover:bg-red-700 text-white font-bold px-6"><ShoppingCart className="w-4 h-4 mr-2" />{isZh ? '加入购物车' : 'Add to Cart'}</Button>
          </div>
          {pkg.image && <div className="max-w-lg mx-auto mb-8"><img src={pkg.image} alt="" className="w-full rounded-xl shadow-lg" /></div>}
          <h2 className="text-xl font-bold mb-4">{isZh ? '包含商品 — 请选择规格' : 'Included Products — Select Specs'}</h2>
          <div className="space-y-4">
            {pkgProducts.map(p => (
              <Card key={p.id} className="shadow-sm"><CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <img src={selectedSpecs[p.id]?.image || p.main_image || ''} alt="" className="w-24 h-24 rounded-lg object-contain bg-gray-50" />
                  <div className="flex-1">
                    <h3 className="font-bold">{t(p.name_en, p.name_zh)}</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-red-600 font-bold">${p.package_price ?? 0} <span className="text-xs text-gray-400 font-normal">{isZh ? '套餐价' : 'pkg'}</span></span>
                      {p.original_price > 0 && p.original_price !== (p.package_price ?? 0) && <span className="text-gray-400 line-through text-sm">${p.original_price}</span>}
                    </div>
                    {p.specs?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {p.specs.map((spec, si) => (
                          <button key={si} onClick={() => setSelectedSpecs(prev => ({...prev, [p.id]: spec}))}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs transition-all ${selectedSpecs[p.id]?.name_en === spec.name_en ? 'border-red-600 bg-red-50' : 'border-gray-200'}`}>
                            {spec.image && <img src={spec.image} alt="" className="w-8 h-auto object-contain rounded" />}
                            <span>{t(spec.name_en, spec.name_zh)}</span>
                            {selectedSpecs[p.id]?.name_en === spec.name_en && <Check className="w-3 h-3 text-red-600" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {p.upgrade_product_id && (
                    <Button variant="outline" size="sm" className="text-amber-600 border-amber-300 hover:bg-amber-50" title={isZh ? '升级' : 'Upgrade'}>
                      <ArrowUpCircle className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent></Card>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Shop;
